<template>
  <section class="px-3 pb-3">
    <AdminToastStack :toasts="toasts" @close="removeToast" />

    <div class="admin-surface">
      <div class="admin-surface__head">
        <span class="admin-surface__title">Цены LLM</span>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-outline-primary btn-sm" :disabled="isLoading" @click="loadSnapshots">
            <i class="bi bi-arrow-repeat me-1"></i>
            Обновить
          </button>
          <button type="button" class="btn btn-primary btn-sm" :disabled="isLoading" @click="openCreateSnapshot">
            <i class="bi bi-plus-lg me-1"></i>
            Новый черновик
          </button>
        </div>
      </div>

      <p class="text-muted small mt-2 mb-0">
        Цены и модели — единый источник для cmaker. Списки моделей в настройках стримов (CMAKER_RUN)
        подставляются из активного снапшота. Редактировать можно только черновики (DRAFT).
      </p>

      <div v-if="errorMessage" class="alert alert-danger py-2 px-3 mt-3 mb-0">{{ errorMessage }}</div>
      <div v-if="successMessage" class="alert alert-success py-2 px-3 mt-3 mb-0">{{ successMessage }}</div>

      <div class="row g-3 mt-1">
        <!-- Snapshots list -->
        <div class="col-12 col-xxl-5">
          <div class="table-responsive admin-table-wrap">
            <table class="table table-sm align-middle mb-0 admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Статус</th>
                  <th>Валюта</th>
                  <th>Актуально на</th>
                  <th>Моделей</th>
                  <th class="text-end">Действия</th>
                </tr>
              </thead>
              <tbody v-if="snapshots.length">
                <tr
                  v-for="snap in snapshots"
                  :key="snap.id"
                  :class="{ 'admin-table__row--active': selectedId === snap.id }"
                  role="button"
                  @click="selectSnapshot(snap)"
                >
                  <td>{{ snap.id }}</td>
                  <td><span class="badge" :class="statusBadgeClass(snap.status)">{{ snap.status }}</span></td>
                  <td>{{ snap.currency }}</td>
                  <td>{{ formatDateTime(snap.sourceAsOf) }}</td>
                  <td>{{ snap.models.length }}</td>
                  <td class="text-end" @click.stop>
                    <div class="btn-group btn-group-sm">
                      <button
                        v-if="snap.status === 'DRAFT'"
                        class="btn btn-outline-success"
                        :disabled="isLoading"
                        @click="publishSnapshot(snap)"
                      >
                        Опубликовать
                      </button>
                      <button
                        v-if="snap.status === 'ACTIVE'"
                        class="btn btn-outline-warning"
                        :disabled="isLoading"
                        @click="archiveSnapshot(snap)"
                      >
                        Архивировать
                      </button>
                      <button
                        v-if="snap.status === 'DRAFT'"
                        class="btn btn-outline-danger"
                        :disabled="isLoading"
                        @click="removeSnapshot(snap)"
                      >
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="6" class="text-center text-muted py-3">Снапшоты цен не найдены.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Detail panel -->
        <div class="col-12 col-xxl-7">
          <!-- Create snapshot form -->
          <div v-if="creatingSnapshot" class="admin-detail mb-3">
            <div class="admin-detail__title">Новый черновик снапшота</div>
            <form class="mt-2" @submit.prevent="submitNewSnapshot">
              <div class="row g-2">
                <div class="col-12 col-lg-4">
                  <label class="form-label mb-1">Валюта *</label>
                  <input v-model.trim="snapshotForm.currency" type="text" maxlength="3" class="form-control form-control-sm" />
                </div>
                <div class="col-12 col-lg-4">
                  <label class="form-label mb-1">Актуально на *</label>
                  <input v-model="snapshotForm.sourceAsOf" type="date" class="form-control form-control-sm" />
                </div>
                <div class="col-12">
                  <label class="form-label mb-1">Комментарий</label>
                  <textarea v-model="snapshotForm.comment" rows="2" class="form-control form-control-sm"></textarea>
                </div>
                <div class="col-12">
                  <div class="form-check">
                    <input id="copyFromActive" v-model="snapshotForm.copyFromActive" type="checkbox" class="form-check-input" />
                    <label for="copyFromActive" class="form-check-label">
                      Скопировать модели и цены из активного снапшота
                    </label>
                  </div>
                </div>
              </div>
              <div class="d-flex justify-content-end gap-2 mt-3">
                <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isLoading" @click="creatingSnapshot = false">
                  Отмена
                </button>
                <button type="submit" class="btn btn-primary btn-sm" :disabled="isLoading">Создать черновик</button>
              </div>
            </form>
          </div>

          <div v-if="selectedSnapshot" class="admin-detail">
            <div class="admin-detail__title d-flex align-items-center justify-content-between">
              <span>Снапшот #{{ selectedSnapshot.id }}</span>
              <span class="badge" :class="statusBadgeClass(selectedSnapshot.status)">{{ selectedSnapshot.status }}</span>
            </div>
            <small class="text-muted">
              Валюта: {{ selectedSnapshot.currency }} · Актуально на: {{ formatDateTime(selectedSnapshot.sourceAsOf) }}
              <template v-if="selectedSnapshot.publishedAt"> · Опубликован: {{ formatDateTime(selectedSnapshot.publishedAt) }}</template>
            </small>
            <p v-if="selectedSnapshot.comment" class="text-muted small mt-1 mb-0">{{ selectedSnapshot.comment }}</p>

            <div v-if="!isDraft" class="alert alert-secondary py-2 px-3 mt-2 mb-0 small">
              Только для чтения. Чтобы изменить цены, создайте новый черновик.
            </div>

            <!-- Model prices table -->
            <div class="table-responsive admin-table-wrap mt-3" style="max-height: 48vh;">
              <table class="table table-sm align-middle mb-0 admin-table">
                <thead>
                  <tr>
                    <th>Тип</th>
                    <th>Провайдер</th>
                    <th>Модель</th>
                    <th class="text-end">in /1M</th>
                    <th class="text-end">out /1M</th>
                    <th class="text-end">/запрос</th>
                    <th>Вкл.</th>
                    <th v-if="isDraft" class="text-end">Действия</th>
                  </tr>
                </thead>
                <tbody v-if="sortedModels.length">
                  <tr
                    v-for="m in sortedModels"
                    :key="m.id"
                    :class="{ 'admin-table__row--active': modelForm.id === m.id }"
                  >
                    <td>{{ m.modelKind }}</td>
                    <td>{{ m.provider }}</td>
                    <td>{{ m.model }}</td>
                    <td class="text-end">{{ fmtMoney(m.inputPer1mUsd) }}</td>
                    <td class="text-end">{{ fmtMoney(m.outputPer1mUsd) }}</td>
                    <td class="text-end">{{ fmtMoney(m.perRequestUsd) }}</td>
                    <td>{{ m.enabled ? 'да' : 'нет' }}</td>
                    <td v-if="isDraft" class="text-end">
                      <div class="btn-group btn-group-sm">
                        <button class="btn btn-outline-primary" @click="editModel(m)">Изменить</button>
                        <button class="btn btn-outline-danger" @click="removeModel(m)">Удалить</button>
                      </div>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td :colspan="isDraft ? 8 : 7" class="text-center text-muted py-3">Цены моделей отсутствуют.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Model price form (DRAFT only) -->
            <form v-if="isDraft" class="mt-3 admin-model-form" @submit.prevent="submitModel">
              <div class="admin-detail__title">{{ modelForm.id ? 'Редактирование цены' : 'Добавить цену модели' }}</div>
              <div class="row g-2 mt-1">
                <div class="col-6 col-lg-3">
                  <label class="form-label mb-1">Тип *</label>
                  <select v-model="modelForm.modelKind" class="form-select form-select-sm" :disabled="Boolean(modelForm.id)">
                    <option v-for="kind in AI_MODEL_KIND_VALUES" :key="kind" :value="kind">{{ kind }}</option>
                  </select>
                </div>
                <div class="col-6 col-lg-3">
                  <label class="form-label mb-1">Провайдер *</label>
                  <input v-model.trim="modelForm.provider" type="text" class="form-control form-control-sm" :disabled="Boolean(modelForm.id)" />
                </div>
                <div class="col-12 col-lg-6">
                  <label class="form-label mb-1">Модель *</label>
                  <input v-model.trim="modelForm.model" type="text" class="form-control form-control-sm" :disabled="Boolean(modelForm.id)" />
                </div>

                <div class="col-6 col-lg-3">
                  <label class="form-label mb-1">input /1M USD</label>
                  <input v-model="modelForm.inputPer1mUsd" type="number" step="any" min="0" class="form-control form-control-sm" />
                </div>
                <div class="col-6 col-lg-3">
                  <label class="form-label mb-1">output /1M USD</label>
                  <input v-model="modelForm.outputPer1mUsd" type="number" step="any" min="0" class="form-control form-control-sm" />
                </div>
                <div class="col-6 col-lg-3">
                  <label class="form-label mb-1">cache read /1M</label>
                  <input v-model="modelForm.cacheReadPer1mUsd" type="number" step="any" min="0" class="form-control form-control-sm" />
                </div>
                <div class="col-6 col-lg-3">
                  <label class="form-label mb-1">cache write /1M</label>
                  <input v-model="modelForm.cacheWritePer1mUsd" type="number" step="any" min="0" class="form-control form-control-sm" />
                </div>
                <div class="col-6 col-lg-3">
                  <label class="form-label mb-1">reasoning /1M</label>
                  <input v-model="modelForm.reasoningPer1mUsd" type="number" step="any" min="0" class="form-control form-control-sm" />
                </div>
                <div class="col-6 col-lg-3">
                  <label class="form-label mb-1">за запрос USD</label>
                  <input v-model="modelForm.perRequestUsd" type="number" step="any" min="0" class="form-control form-control-sm" />
                </div>
                <div class="col-6 col-lg-3">
                  <label class="form-label mb-1">Действует с</label>
                  <input v-model="modelForm.effectiveFrom" type="date" class="form-control form-control-sm" />
                </div>
                <div class="col-6 col-lg-3 d-flex align-items-end">
                  <div class="form-check">
                    <input id="modelEnabled" v-model="modelForm.enabled" type="checkbox" class="form-check-input" />
                    <label for="modelEnabled" class="form-check-label">Включена</label>
                  </div>
                </div>
              </div>
              <div class="d-flex justify-content-end gap-2 mt-3">
                <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isLoading" @click="resetModelForm">Очистить</button>
                <button type="submit" class="btn btn-primary btn-sm" :disabled="isLoading">
                  {{ modelForm.id ? 'Сохранить' : 'Добавить' }}
                </button>
              </div>
            </form>
          </div>

          <div v-else-if="!creatingSnapshot" class="admin-detail text-muted">
            Выберите снапшот слева, чтобы посмотреть цены моделей.
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import AdminToastStack from '@/admin/components/AdminToastStack.vue'
import { useAdminRequestState } from '@/admin/composables/useAdminRequestState'
import {
  AI_MODEL_KIND_VALUES,
  validateAiModelPriceCreateRequest,
  validateAiPricingSnapshotRequest,
} from '@/admin/types/adminContracts'
import {
  getAiPricingSnapshots,
  createAiPricingSnapshot,
  deleteAiPricingSnapshot,
  publishAiPricingSnapshot,
  archiveAiPricingSnapshot,
  addAiModelPrice,
  updateAiModelPrice,
  deleteAiModelPrice,
} from '@/admin/api/adminService'
import { formatDateTime } from '@/admin/utils/adminApiUtils'

const snapshots = ref([])
const selectedId = ref(null)
const creatingSnapshot = ref(false)

const snapshotForm = reactive({
  currency: 'USD',
  sourceAsOf: '',
  comment: '',
  copyFromActive: true,
})

const emptyModelForm = () => ({
  id: null,
  provider: '',
  model: '',
  modelKind: 'llm',
  inputPer1mUsd: '',
  outputPer1mUsd: '',
  cacheReadPer1mUsd: '',
  cacheWritePer1mUsd: '',
  reasoningPer1mUsd: '',
  perRequestUsd: '',
  effectiveFrom: '',
  enabled: true,
})

const modelForm = reactive(emptyModelForm())

const {
  isLoading,
  errorMessage,
  successMessage,
  toasts,
  removeToast,
  clearMessages,
  pushError,
  execute,
} = useAdminRequestState()

const selectedSnapshot = computed(() => snapshots.value.find(s => s.id === selectedId.value) || null)
const isDraft = computed(() => selectedSnapshot.value?.status === 'DRAFT')

const sortedModels = computed(() => {
  const models = selectedSnapshot.value?.models || []
  return [...models].sort((a, b) => {
    const byKind = String(a.modelKind).localeCompare(String(b.modelKind))
    if (byKind !== 0) return byKind
    const byProvider = String(a.provider).localeCompare(String(b.provider))
    if (byProvider !== 0) return byProvider
    return String(a.model).localeCompare(String(b.model))
  })
})

const statusBadgeClass = status => {
  if (status === 'ACTIVE') return 'text-bg-success'
  if (status === 'DRAFT') return 'text-bg-secondary'
  return 'text-bg-light'
}

const fmtMoney = value => (value == null ? '—' : String(value))

const selectSnapshot = snap => {
  selectedId.value = snap.id
  creatingSnapshot.value = false
  resetModelForm()
}

const resetModelForm = () => {
  Object.assign(modelForm, emptyModelForm())
}

const editModel = m => {
  modelForm.id = m.id
  modelForm.provider = m.provider
  modelForm.model = m.model
  modelForm.modelKind = m.modelKind || 'llm'
  modelForm.inputPer1mUsd = m.inputPer1mUsd == null ? '' : m.inputPer1mUsd
  modelForm.outputPer1mUsd = m.outputPer1mUsd == null ? '' : m.outputPer1mUsd
  modelForm.cacheReadPer1mUsd = m.cacheReadPer1mUsd == null ? '' : m.cacheReadPer1mUsd
  modelForm.cacheWritePer1mUsd = m.cacheWritePer1mUsd == null ? '' : m.cacheWritePer1mUsd
  modelForm.reasoningPer1mUsd = m.reasoningPer1mUsd == null ? '' : m.reasoningPer1mUsd
  modelForm.perRequestUsd = m.perRequestUsd == null ? '' : m.perRequestUsd
  modelForm.effectiveFrom = m.effectiveFrom ? String(m.effectiveFrom).slice(0, 10) : ''
  modelForm.enabled = m.enabled !== false
}

const loadSnapshots = async () => {
  clearMessages()
  const list = await execute(() => getAiPricingSnapshots(), {
    error: 'Не удалось получить снапшоты цен.',
  })
  snapshots.value = list
  if (selectedId.value && !list.some(s => s.id === selectedId.value)) {
    selectedId.value = null
  }
}

const openCreateSnapshot = () => {
  snapshotForm.currency = 'USD'
  snapshotForm.sourceAsOf = new Date().toISOString().slice(0, 10)
  snapshotForm.comment = ''
  snapshotForm.copyFromActive = true
  creatingSnapshot.value = true
}

const submitNewSnapshot = async () => {
  clearMessages()
  const errors = validateAiPricingSnapshotRequest(snapshotForm)
  if (errors.length) {
    pushError(errors[0])
    return
  }
  const created = await execute(() => createAiPricingSnapshot(snapshotForm), {
    success: 'Черновик снапшота создан.',
    error: 'Не удалось создать снапшот.',
  })
  creatingSnapshot.value = false
  await loadSnapshots()
  if (created?.id) selectedId.value = created.id
}

const publishSnapshot = async snap => {
  if (!window.confirm(`Опубликовать снапшот #${snap.id}? Текущий активный будет заархивирован.`)) return
  clearMessages()
  await execute(() => publishAiPricingSnapshot(snap.id), {
    success: 'Снапшот опубликован.',
    error: 'Не удалось опубликовать снапшот.',
  })
  await loadSnapshots()
}

const archiveSnapshot = async snap => {
  if (!window.confirm(`Архивировать снапшот #${snap.id}?`)) return
  clearMessages()
  await execute(() => archiveAiPricingSnapshot(snap.id), {
    success: 'Снапшот заархивирован.',
    error: 'Не удалось заархивировать снапшот.',
  })
  await loadSnapshots()
}

const removeSnapshot = async snap => {
  if (!window.confirm(`Удалить черновик #${snap.id}?`)) return
  clearMessages()
  await execute(() => deleteAiPricingSnapshot(snap.id), {
    success: 'Черновик удалён.',
    error: 'Не удалось удалить черновик.',
  })
  if (selectedId.value === snap.id) selectedId.value = null
  await loadSnapshots()
}

const submitModel = async () => {
  if (!selectedSnapshot.value) return
  clearMessages()
  if (!modelForm.id) {
    const errors = validateAiModelPriceCreateRequest(modelForm)
    if (errors.length) {
      pushError(errors[0])
      return
    }
  }
  const snapshotId = selectedSnapshot.value.id
  if (modelForm.id) {
    await execute(() => updateAiModelPrice(snapshotId, modelForm.id, modelForm), {
      success: 'Цена модели обновлена.',
      error: 'Не удалось обновить цену модели.',
    })
  } else {
    await execute(() => addAiModelPrice(snapshotId, modelForm), {
      success: 'Цена модели добавлена.',
      error: 'Не удалось добавить цену модели.',
    })
  }
  resetModelForm()
  await loadSnapshots()
}

const removeModel = async m => {
  if (!selectedSnapshot.value) return
  if (!window.confirm(`Удалить цену ${m.provider}/${m.model}?`)) return
  clearMessages()
  await execute(() => deleteAiModelPrice(selectedSnapshot.value.id, m.id), {
    success: 'Цена модели удалена.',
    error: 'Не удалось удалить цену модели.',
  })
  if (modelForm.id === m.id) resetModelForm()
  await loadSnapshots()
}

onMounted(async () => {
  await loadSnapshots()
})
</script>

<style scoped>
.admin-surface {
  border-radius: 14px;
  border: 1px solid #dce7fa;
  background: #fff;
  padding: 12px;
}

.admin-surface__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.admin-surface__title {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid #d8e5fb;
  background: #eff5ff;
  color: #3b5690;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 5px 9px;
}

.admin-table-wrap {
  border: 1px solid #e7edf8;
  border-radius: 12px;
  max-height: 72vh;
  overflow: auto;
}

.admin-table thead th {
  position: sticky;
  top: 0;
  background: #f8fbff;
  z-index: 2;
  color: #607089;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid #e9edf6;
}

.admin-table__row--active td {
  background: #eef5ff;
}

.admin-detail {
  border: 1px solid #e4ebf8;
  border-radius: 12px;
  background: #fbfdff;
  padding: 10px;
}

.admin-detail__title {
  color: #1f2d45;
  font-weight: 700;
}
</style>
